import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';

import PageTitle from '../../components/common/PageTitle';
import { educationTaxonomyService } from '../../services';

const EMPTY_FORM = {
  type: 'subject',
  code: '',
  title: '',
  order: 1,
  gradeCodes: [],
  subjectCode: '',
};

const getSelectedValues = event => Array.from(event.target.options)
  .filter(option => option.selected)
  .map(option => option.value);

const EducationTaxonomy = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const loadItems = useCallback(async () => {
    setBusy(true);
    setMessage('');
    try {
      const result = await educationTaxonomyService.findAll({});
      setItems(Array.isArray(result) ? result : []);
    } catch (error) {
      setMessage(error.error || error.message || 'Unable to load education taxonomy.');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const grades = useMemo(() => items.filter(item => item.type === 'grade'), [items]);
  const subjects = useMemo(() => items.filter(item => item.type === 'subject'), [items]);
  const topics = useMemo(() => items.filter(item => item.type === 'topic'), [items]);

  const createItem = async event => {
    event.preventDefault();
    setBusy(true);
    setMessage('');
    try {
      const payload = {
        ...form,
        order: Number(form.order) || 0,
        active: true,
        gradeCodes: form.gradeCodes,
      };
      if (form.type !== 'topic') delete payload.subjectCode;
      await educationTaxonomyService.create(payload);
      setForm(EMPTY_FORM);
      setMessage('Educational item created successfully.');
      await loadItems();
    } catch (error) {
      setMessage(error.error || error.message || 'Unable to create educational item.');
    } finally {
      setBusy(false);
    }
  };

  const updateItem = async (item, changes) => {
    setBusy(true);
    setMessage('');
    try {
      await educationTaxonomyService.update(item._id, changes);
      await loadItems();
    } catch (error) {
      setMessage(error.error || error.message || 'Unable to update educational item.');
      setBusy(false);
    }
  };

  const toggleGrade = gradeCode => {
    setForm(current => ({
      ...current,
      gradeCodes: current.gradeCodes.includes(gradeCode)
        ? current.gradeCodes.filter(code => code !== gradeCode)
        : [...current.gradeCodes, gradeCode],
    }));
  };

  const renderItems = (title, collection) => (
    <section className="mb-4">
      <h5>{title}</h5>
      <div className="table-responsive">
        <table className="table table-sm table-bordered bg-white">
          <thead>
            <tr>
              <th>Title</th><th>Code</th><th>Order</th><th>Subject</th><th>Grades</th><th>Status</th><th>Action</th>
            </tr>
          </thead>
          <tbody>
            {collection.map(item => (
              <tr key={item._id}>
                <td>
                  <input
                    aria-label={`${item.code} title`}
                    defaultValue={item.title}
                    onBlur={event => event.target.value !== item.title && updateItem(item, { title: event.target.value })}
                  />
                </td>
                <td><code>{item.code}</code></td>
                <td>
                  <input
                    aria-label={`${item.code} order`}
                    type="number"
                    min="0"
                    defaultValue={item.order}
                    onBlur={event => Number(event.target.value) !== item.order && updateItem(item, { order: Number(event.target.value) })}
                    style={{ width: 80 }}
                  />
                </td>
                <td>
                  {item.type === 'topic' ? (
                    <select
                      aria-label={`${item.code} subject`}
                      value={item.subjectCode || ''}
                      onChange={event => updateItem(item, { subjectCode: event.target.value })}
                    >
                      <option value="">Choose...</option>
                      {subjects.filter(subject => subject.active).map(subject => (
                        <option key={subject.code} value={subject.code}>{subject.title}</option>
                      ))}
                    </select>
                  ) : '—'}
                </td>
                <td>
                  {item.type === 'grade' ? '—' : (
                    <select
                      multiple
                      aria-label={`${item.code} grades`}
                      value={item.gradeCodes || []}
                      onChange={event => updateItem(item, { gradeCodes: getSelectedValues(event) })}
                      style={{ minWidth: 150, minHeight: 84 }}
                    >
                      {grades.filter(grade => grade.active).map(grade => (
                        <option key={grade.code} value={grade.code}>{grade.title}</option>
                      ))}
                    </select>
                  )}
                  {item.type !== 'grade' && !(item.gradeCodes || []).length && (
                    <small className="d-block text-muted">Empty = all grades</small>
                  )}
                </td>
                <td>{item.active ? 'Active' : 'Inactive'}</td>
                <td>
                  <Button
                    size="sm"
                    variant={item.active ? 'outline-danger' : 'outline-success'}
                    disabled={busy}
                    onClick={() => updateItem(item, { active: !item.active })}
                  >
                    {item.active ? 'Deactivate' : 'Activate'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );

  return (
    <Container fluid className="main-content-container px-4">
      <Row noGutters className="page-header py-4">
        <PageTitle sm="6" title="Education Structure" subtitle="School" className="text-sm-left" />
      </Row>

      {message && <div className="alert alert-info">{message}</div>}

      <form className="bg-white border rounded p-3 mb-4" onSubmit={createItem}>
        <h5>Add a subject or topic</h5>
        <Row>
          <Col md="2">
            <label htmlFor="taxonomyType">Type</label>
            <select id="taxonomyType" value={form.type} onChange={event => setForm({ ...EMPTY_FORM, type: event.target.value })}>
              <option value="subject">Subject</option>
              <option value="topic">Topic</option>
            </select>
          </Col>
          <Col md="3">
            <label htmlFor="taxonomyTitle">Persian title</label>
            <input id="taxonomyTitle" required value={form.title} onChange={event => setForm({ ...form, title: event.target.value })} />
          </Col>
          <Col md="3">
            <label htmlFor="taxonomyCode">Stable code (English)</label>
            <input id="taxonomyCode" required pattern="[a-z0-9-]+" value={form.code} onChange={event => setForm({ ...form, code: event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} />
          </Col>
          <Col md="2">
            <label htmlFor="taxonomyOrder">Order</label>
            <input id="taxonomyOrder" type="number" min="0" value={form.order} onChange={event => setForm({ ...form, order: event.target.value })} />
          </Col>
          {form.type === 'topic' && (
            <Col md="2">
              <label htmlFor="taxonomySubject">Subject</label>
              <select id="taxonomySubject" required value={form.subjectCode} onChange={event => setForm({ ...form, subjectCode: event.target.value })}>
                <option value="">Choose...</option>
                {subjects.filter(item => item.active).map(item => <option key={item.code} value={item.code}>{item.title}</option>)}
              </select>
            </Col>
          )}
        </Row>
        <div className="my-3">
          <strong>Available for grades:</strong>{' '}
          <span className="text-muted">Leave empty to use for every grade.</span>
          <div className="d-flex flex-wrap mt-2">
            {grades.filter(item => item.active).map(grade => (
              <label key={grade.code} className="mr-3 mb-2">
                <input type="checkbox" checked={form.gradeCodes.includes(grade.code)} onChange={() => toggleGrade(grade.code)} />{' '}
                {grade.title}
              </label>
            ))}
          </div>
        </div>
        <Button type="submit" variant="success" disabled={busy}>Add</Button>
      </form>

      {renderItems('Grades (fixed 1600 × 1600)', grades)}
      {renderItems('Subjects', subjects)}
      {renderItems('Topics', topics)}
    </Container>
  );
};

export default EducationTaxonomy;
